/** What can be said about an image file beyond its pixel size. */
export type ImageFacts = {
  /** Bits per channel as stored. 8 unless the container says otherwise. */
  readonly bitDepth: number;
  /** Whether the format carries an alpha channel at all. */
  readonly hasAlpha: boolean;
  /** Whether any pixel is actually not fully opaque. */
  readonly usesTransparency: boolean;
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
/** PNG colour types that carry an alpha sample. 3 is a palette, which may have tRNS. */
const PNG_ALPHA_TYPES = new Set([4, 6]);

/**
 * Reads the IHDR of a PNG, which is the only common web format that varies its bit depth.
 * JPEG and WebP are 8 bits per channel in every profile a browser will decode.
 */
export function readPngHeader(bytes: Uint8Array): { bitDepth: number; colorType: number } | null {
  if (bytes.length < 26) return null;
  if (PNG_SIGNATURE.some((value, index) => bytes[index] !== value)) return null;
  const bitDepth = bytes[24];
  const colorType = bytes[25];
  if (bitDepth === undefined || colorType === undefined) return null;
  return { bitDepth, colorType };
}

/** True as soon as one pixel is not fully opaque; stops at the first one it finds. */
export function scanForTransparency(pixels: Uint8ClampedArray): boolean {
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] !== 255) return true;
  }
  return false;
}

export async function readImageFacts(file: File, bitmap: ImageBitmap): Promise<ImageFacts> {
  const header = readPngHeader(new Uint8Array(await file.slice(0, 32).arrayBuffer()));
  const bitDepth = header?.bitDepth ?? 8;
  const hasAlpha =
    header === null
      ? file.type !== "image/jpeg"
      : PNG_ALPHA_TYPES.has(header.colorType) || header.colorType === 3;

  return {
    bitDepth,
    hasAlpha,
    usesTransparency: hasAlpha && (await hasTransparentPixel(bitmap)),
  };
}

async function hasTransparentPixel(bitmap: ImageBitmap): Promise<boolean> {
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return false;
    context.drawImage(bitmap, 0, 0);
    return scanForTransparency(context.getImageData(0, 0, bitmap.width, bitmap.height).data);
  } catch {
    // A very large image can fail to allocate; the channel's presence is still reported.
    return false;
  }
}
